import React, { useEffect, useRef } from 'react';
import { Calendar, Ticket, Users, Star, Zap, Music } from 'lucide-react';

interface FloatingElementsProps {
  density?: 'low' | 'medium' | 'high';
  className?: string;
}

export const FloatingElements: React.FC<FloatingElementsProps> = ({
  density = 'medium',
  className = ''
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const icons = [Calendar, Ticket, Users, Star, Zap, Music];
  
  const getElementCount = () => {
    switch (density) {
      case 'low': return 8;
      case 'medium': return 12;
      case 'high': return 16;
      default: return 12;
    }
  };

  const generateFloatingElement = (index: number) => {
    const Icon = icons[index % icons.length];
    const size = Math.random() * 20 + 16; // 16-36px
    const left = Math.random() * 100;
    const top = Math.random() * 100;
    const animationDuration = Math.random() * 20 + 15; // 15-35s
    const animationDelay = Math.random() * 10; // 0-10s
    const opacity = Math.random() * 0.3 + 0.1; // 0.1-0.4

    return (
      <div
        key={index}
        className="absolute pointer-events-none"
        style={{
          left: `${left}%`,
          top: `${top}%`,
          animation: `float ${animationDuration}s ease-in-out infinite`,
          animationDelay: `${animationDelay}s`,
          opacity
        }}
      >
        <Icon 
          size={size} 
          className="text-orange-primary/30 dark:text-orange-primary/20"
          style={{
            filter: 'blur(0.5px)',
            animation: `subtle-float ${animationDuration * 0.8}s ease-in-out infinite reverse`,
            animationDelay: `${animationDelay * 0.5}s`
          }}
        />
      </div>
    );
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Add mouse interaction
    const handleMouseMove = (e: MouseEvent) => {
      const rect = container.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;

      const elements = container.querySelectorAll('.floating-icon');
      elements.forEach((element, index) => {
        const el = element as HTMLElement;
        const distance = Math.sqrt(
          Math.pow(x - parseFloat(el.dataset.x || '0'), 2) + 
          Math.pow(y - parseFloat(el.dataset.y || '0'), 2)
        );
        
        if (distance < 0.2) {
          const force = (0.2 - distance) * 50;
          const angle = Math.atan2(
            y - parseFloat(el.dataset.y || '0'), 
            x - parseFloat(el.dataset.x || '0')
          );
          
          el.style.transform = `translate(${-Math.cos(angle) * force}px, ${-Math.sin(angle) * force}px) scale(1.1)`;
        } else {
          el.style.transform = 'translate(0, 0) scale(1)';
        }
      });
    };

    container.addEventListener('mousemove', handleMouseMove);

    return () => {
      container.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  return (
    <div 
      ref={containerRef}
      className={`fixed inset-0 overflow-hidden pointer-events-none -z-10 ${className}`}
    >
      {Array.from({ length: getElementCount() }, (_, index) => generateFloatingElement(index))}
      
      {/* Floating geometric shapes */}
      <div
        className="absolute pointer-events-none"
        style={{
          left: '20%',
          top: '30%',
          width: '4px',
          height: '4px',
          background: 'rgba(255, 140, 0, 0.3)',
          borderRadius: '50%',
          animation: 'float-3 25s ease-in-out infinite',
          animationDelay: '2s'
        }}
      />
      
      <div
        className="absolute pointer-events-none"
        style={{
          right: '25%',
          top: '60%',
          width: '6px',
          height: '6px',
          background: 'rgba(255, 140, 0, 0.2)',
          borderRadius: '2px',
          animation: 'float-1 20s ease-in-out infinite',
          animationDelay: '5s',
          transform: 'rotate(45deg)'
        }}
      />
      
      <div
        className="absolute pointer-events-none"
        style={{
          left: '70%',
          top: '20%',
          width: '8px',
          height: '1px',
          background: 'rgba(255, 140, 0, 0.25)',
          animation: 'float-2 18s ease-in-out infinite',
          animationDelay: '7s'
        }}
      />
      
      {/* Floating text elements */}
      <div
        className="absolute pointer-events-none text-xs text-orange-primary/20 font-medium"
        style={{
          left: '15%',
          top: '70%',
          animation: 'float-1 30s ease-in-out infinite',
          animationDelay: '10s',
          filter: 'blur(0.5px)'
        }}
      >
        EVENTOS
      </div>
      
      <div
        className="absolute pointer-events-none text-xs text-orange-primary/15 font-medium"
        style={{
          right: '20%',
          top: '40%',
          animation: 'float-3 25s ease-in-out infinite reverse',
          animationDelay: '15s',
          filter: 'blur(0.5px)'
        }}
      >
        INGRESSOS
      </div>
    </div>
  );
};

// Specialized floating elements for specific sections
export const HeroFloatingElements: React.FC = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Large central floating element */}
      <div
        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
        style={{
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(255, 140, 0, 0.05) 0%, transparent 70%)',
          borderRadius: '50%',
          animation: 'subtle-float 20s ease-in-out infinite',
          filter: 'blur(3px)'
        }}
      />
      
      {/* Orbiting elements */}
      {[...Array(6)].map((_, index) => (
        <div
          key={index}
          className="absolute top-1/2 left-1/2"
          style={{
            width: '4px',
            height: '4px',
            background: 'rgba(255, 140, 0, 0.4)',
            borderRadius: '50%',
            transformOrigin: '0 0',
            animation: `orbit ${15 + index * 2}s linear infinite`,
            animationDelay: `${index * 2}s`,
            transform: `translate(-2px, -2px) rotate(${index * 60}deg) translateX(150px)`
          }}
        />
      ))}
    </div>
  );
};